<template>
  <div class="rd-screen">
    <report-tools :copy="dayRows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading && !days.length" kind="loading"></report-state>
    <report-state v-else-if="!dayRows.length" icon="mdi-cash-multiple" :title="emptyTitle" :text="emptyText"></report-state>

    <div v-else class="ui-card rd-card">
      <table class="ui-table rd-tbl rd-inc">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num"></th>
            <th scope="col">{{ $t("date") }}</th>
            <th scope="col" class="is-right">{{ $t("number_of_orders") }}</th>
            <th scope="col" class="is-right rd-col-share">{{ $t("amount") }}</th>
          </tr>
        </thead>
        <tbody v-for="day in dayRows" :key="day.date">
          <tr data-open :class="{ 'is-selected': isOpen(day) }" @click="toggle(day)">
            <td>
              <button
                type="button"
                class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain"
                :aria-expanded="String(isOpen(day))"
                :aria-label="`${$t('view_orders')} (${day.orders.length})`"
                @click.stop="toggle(day)"
              >
                <v-icon size="18">{{ isOpen(day) ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
              </button>
            </td>
            <td class="is-name">{{ day.title }}</td>
            <td class="is-right rd-num">{{ day.orders.length }}</td>
            <td class="is-right rd-num">
              <span class="rd-share">
                <b>{{ day.amountText }}</b>
                <span class="rd-mini"><i :style="{ width: `${day.share}%` }"></i></span>
              </span>
            </td>
          </tr>
          <tr v-if="isOpen(day)" class="rd-inc__orders">
            <td colspan="4">
              <table class="ui-table rd-tbl rd-tbl--inner">
                <colgroup>
                  <col class="rd-inc__c-id" />
                  <col class="rd-inc__c-time" />
                  <col />
                  <col class="rd-inc__c-status" />
                  <col class="rd-inc__c-amount" />
                </colgroup>
                <tbody>
                  <tr v-for="order in day.orders" :key="order.id">
                    <td class="rd-num">
                      <button type="button" class="ui-text-btn" :aria-label="$t('order_details')" @click="open(order, $event)">
                        #{{ order.id }}
                      </button>
                    </td>
                    <td class="rd-num">{{ order.time || "–" }}</td>
                    <td>
                      <span class="rd-inc__name">{{ order.displayName }}</span>
                      <span class="rd-muted rd-num">{{ order.phone }}</span>
                    </td>
                    <td>
                      <span class="rd-sbadge rd-sbadge--static" :style="badgeStyle(order.state)">
                        <i class="rd-dot" :style="dotStyle(order.state)"></i>
                        {{ $t(order.state) }}
                      </span>
                    </td>
                    <td class="is-right rd-num">
                      <b>{{ order.amountText }}</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td class="is-right rd-num">{{ orderCount }}</td>
            <td class="is-right rd-num">{{ totalText }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import hasGuestOrderStatus from "@/mixins/report/hasGuestOrderStatus";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import { orderTotal } from "@/utils/guestOrderTotal";
import { menuState } from "@/utils/guestOrderStatus";

/**
 * Prihodi: iznos po danima sa trakom prema najboljem danu; dan se širi u spisak narudžbi, a
 * narudžba otvara panel. Ne učitava ništa; podatke daje ekran koji ga koristi (stari tab sa
 * nizom { date, amount, orders }).
 */
export default {
  name: "GuestIncomeView",
  mixins: [hasGuestOrderStatus],
  components: { ReportState, ReportTools },
  props: {
    days: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    search: { type: String, default: "" },
  },
  data: () => ({
    openDays: {},
  }),
  computed: {
    /** Dani sa narudžbama koje odgovaraju pretrazi; iznos po valuti restorana čija je narudžba. */
    dayRows() {
      const rows = this.days
        .map((day) => {
          const orders = this.customSearch(
            day.orders.map((order) => this.toOrder(order)),
            [{ value: "displayName" }, { value: "phone" }, { value: "idText" }],
            this.search
          );
          return { date: day.date, orders, parts: this.partsOf(orders) };
        })
        .filter((day) => day.orders.length);
      const max = Math.max(0, ...rows.map((day) => day.parts.reduce((sum, part) => sum + part.amount, 0)));
      return rows.map((day) => {
        const amount = day.parts.reduce((sum, part) => sum + part.amount, 0);
        return {
          ...day,
          title: this.dayTitle(day.date),
          amountText: this.partsText(day.parts),
          share: max ? Math.round((amount / max) * 100) : 0,
        };
      });
    },
    allOrders() {
      return this.dayRows.flatMap((day) => day.orders);
    },
    orderCount() {
      return this.allOrders.length;
    },
    totalParts() {
      return this.partsOf(this.allOrders);
    },
    totalText() {
      return this.partsText(this.totalParts);
    },
    emptyTitle() {
      return this.search
        ? this.$t("report_mobile.no_results_for", { search: this.search })
        : this.$t("no_income_data");
    },
    emptyText() {
      return this.search ? "" : this.$t("no_income_found");
    },
    kpis() {
      const total = this.totalParts.reduce((sum, part) => sum + part.amount, 0);
      const currencies = this.totalParts.map((part) => part.currency);
      const average = this.orderCount && currencies.length === 1 ? `${(total / this.orderCount).toFixed(2)} ${currencies[0]}` : "";
      return [
        { key: "total", label: this.$t("total"), value: this.totalText, tone: "inverse" },
        { key: "orders", label: this.$t("orders"), value: this.orderCount },
        ...(average ? [{ key: "average", label: this.$t("guest_desktop.average"), value: average, tone: "accent" }] : []),
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.orderCount ? kpis : []);
      },
      immediate: true,
    },
  },
  methods: {
    /** Red za prikaz; `source` je izvorna narudžba (nju menja promena statusa, pa se prikaz sam osvežava). */
    toOrder(order) {
      const amount = orderTotal(order);
      const currency = order.restaurant?.settings?.price || this.restaurantCurrency;
      return {
        source: order,
        id: order.id,
        phone: order.phone,
        state: menuState(order.state),
        idText: String(order.id),
        time: this.formatOrderTime(order),
        displayName: `${this.toLatin(order.name || "")} ${this.toLatin(order.lastname || "")}`.trim(),
        amount,
        currency,
        amountText: `${amount.toFixed(2)} ${currency}`,
      };
    },
    /** [{ currency, amount }]: različite valute se ne sabiraju. */
    partsOf(orders) {
      const buckets = {};
      orders.forEach((order) => {
        buckets[order.currency] = (buckets[order.currency] || 0) + order.amount;
      });
      return Object.entries(buckets).map(([currency, amount]) => ({ currency, amount }));
    },
    partsText(parts) {
      return parts.length ? parts.map((part) => `${part.amount.toFixed(2)} ${part.currency}`).join(" + ") : `0.00 ${this.restaurantCurrency}`;
    },
    badgeStyle(state) {
      const colors = this.getStatusColors(state);
      return { backgroundColor: colors.bg, color: colors.fg };
    },
    dotStyle(state) {
      return { backgroundColor: this.getStatusColors(state).fg };
    },
    dayTitle(date) {
      const day = moment(date, "YYYY-MM-DD");
      const weekday = day.toDate().toLocaleDateString(this.$i18n.locale === "en" ? "en" : "sr-Latn", { weekday: "long" });
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day.format("DD.MM.YYYY.")}`;
    },
    /** Jedan dan je odmah raširen; kad ih ima više, svaki se širi klikom. */
    isOpen(day) {
      const state = this.openDays[day.date];
      return state === undefined ? this.dayRows.length === 1 : state;
    },
    toggle(day) {
      this.$set(this.openDays, day.date, !this.isOpen(day));
    },
    open(order, event) {
      this.$emit("open-order", { order: order.source, guest: order.source, event });
    },
    async copyTable() {
      const headers = [this.$t("date"), this.$t("number_of_orders"), this.$t("amount")];
      const rows = this.dayRows.map((day) => [day.date, day.orders.length, day.amountText]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
